/**
 * Game initialization
 */
import { getRandomAugmentationChoices } from "./augmentations/registry";
import type {
  AugmentationSelectionState,
  PlayerAugmentations,
} from "./types/augmentation";
import type { Board, BoardSquare } from "./types/board";
import type { Color, File, PieceType, Rank, Square } from "./types/common";
import { FILES, RANKS } from "./types/common";
import type { CastlingRights, ChessState } from "./types/game-state";
import type { Piece } from "./types/piece";
import { AUGMENTATION_CHOICES_PER_ROUND } from "./utils/augmentation-schedule";
import { getPositionHash } from "./utils/fen";

/** Piece template for initial setup */
interface PieceTemplate {
  type: PieceType;
  color: Color;
  position: Square;
}

/** Standard chess starting position - 32 pieces */
const INITIAL_PIECES: PieceTemplate[] = [
  // White back rank (rank 1)
  { type: "ROOK", color: "WHITE", position: { file: "a", rank: 1 } },
  { type: "KNIGHT", color: "WHITE", position: { file: "b", rank: 1 } },
  { type: "BISHOP", color: "WHITE", position: { file: "c", rank: 1 } },
  { type: "QUEEN", color: "WHITE", position: { file: "d", rank: 1 } },
  { type: "KING", color: "WHITE", position: { file: "e", rank: 1 } },
  { type: "BISHOP", color: "WHITE", position: { file: "f", rank: 1 } },
  { type: "KNIGHT", color: "WHITE", position: { file: "g", rank: 1 } },
  { type: "ROOK", color: "WHITE", position: { file: "h", rank: 1 } },
  // White pawns (rank 2)
  { type: "PAWN", color: "WHITE", position: { file: "a", rank: 2 } },
  { type: "PAWN", color: "WHITE", position: { file: "b", rank: 2 } },
  { type: "PAWN", color: "WHITE", position: { file: "c", rank: 2 } },
  { type: "PAWN", color: "WHITE", position: { file: "d", rank: 2 } },
  { type: "PAWN", color: "WHITE", position: { file: "e", rank: 2 } },
  { type: "PAWN", color: "WHITE", position: { file: "f", rank: 2 } },
  { type: "PAWN", color: "WHITE", position: { file: "g", rank: 2 } },
  { type: "PAWN", color: "WHITE", position: { file: "h", rank: 2 } },
  // Black back rank (rank 8)
  { type: "ROOK", color: "BLACK", position: { file: "a", rank: 8 } },
  { type: "KNIGHT", color: "BLACK", position: { file: "b", rank: 8 } },
  { type: "BISHOP", color: "BLACK", position: { file: "c", rank: 8 } },
  { type: "QUEEN", color: "BLACK", position: { file: "d", rank: 8 } },
  { type: "KING", color: "BLACK", position: { file: "e", rank: 8 } },
  { type: "BISHOP", color: "BLACK", position: { file: "f", rank: 8 } },
  { type: "KNIGHT", color: "BLACK", position: { file: "g", rank: 8 } },
  { type: "ROOK", color: "BLACK", position: { file: "h", rank: 8 } },
  // Black pawns (rank 7)
  { type: "PAWN", color: "BLACK", position: { file: "a", rank: 7 } },
  { type: "PAWN", color: "BLACK", position: { file: "b", rank: 7 } },
  { type: "PAWN", color: "BLACK", position: { file: "c", rank: 7 } },
  { type: "PAWN", color: "BLACK", position: { file: "d", rank: 7 } },
  { type: "PAWN", color: "BLACK", position: { file: "e", rank: 7 } },
  { type: "PAWN", color: "BLACK", position: { file: "f", rank: 7 } },
  { type: "PAWN", color: "BLACK", position: { file: "g", rank: 7 } },
  { type: "PAWN", color: "BLACK", position: { file: "h", rank: 7 } },
];

/** Generate a unique game ID */
function generateGameId(): string {
  return `game-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/** Generate a unique piece ID */
function generatePieceId(template: PieceTemplate, index: number): string {
  return `${template.color.toLowerCase()}-${template.type.toLowerCase()}-${index}`;
}

/** Create pieces from templates */
function createPieces(): Piece[] {
  return INITIAL_PIECES.map((template, index) => ({
    id: generatePieceId(template, index),
    type: template.type,
    color: template.color,
    position: { ...template.position },
    hasMoved: false,
  }));
}

/** Create empty board with piece placements */
function createBoard(pieces: Piece[]): Board {
  const squares: BoardSquare[][] = [];

  for (let fi = 0; fi < 8; fi++) {
    squares[fi] = [];
    for (let ri = 0; ri < 8; ri++) {
      const file = FILES[fi] as File;
      const rank = RANKS[ri] as Rank;
      const position: Square = { file, rank };

      // Light squares: a1 is dark, so (0+0)%2=0 is dark
      // Light square when (fileIndex + rankIndex) is odd
      const isLightSquare = (fi + ri) % 2 === 1;

      // Find piece at this position
      const piece = pieces.find(
        (p) => p.position.file === file && p.position.rank === rank,
      );

      squares[fi]![ri] = {
        position,
        color: isLightSquare ? "LIGHT" : "DARK",
        pieceId: piece?.id ?? null,
      };
    }
  }

  return { squares };
}

/** Create initial castling rights (all available) */
function createCastlingRights(): CastlingRights {
  return {
    whiteKingside: true,
    whiteQueenside: true,
    blackKingside: true,
    blackQueenside: true,
  };
}

/** Create initial player augmentation state */
function createPlayerAugmentations(): PlayerAugmentations {
  return {
    active: [],
    pendingSelection: null,
  };
}

/** Create initial augmentation selection state */
function createAugmentationSelection(): AugmentationSelectionState {
  return {
    whiteChoices: getRandomAugmentationChoices(AUGMENTATION_CHOICES_PER_ROUND),
    blackChoices: getRandomAugmentationChoices(AUGMENTATION_CHOICES_PER_ROUND),
    whiteSelected: false,
    blackSelected: false,
  };
}

/**
 * Create initial game state with standard chess starting position
 */
export function createInitialState(): ChessState {
  const pieces = createPieces();
  const board = createBoard(pieces);
  const now = new Date().toISOString();

  // Create state without positionHistory first
  const state: ChessState = {
    id: generateGameId(),
    version: "1.0.0",
    board,
    pieces,
    currentPlayer: "WHITE",
    turnNumber: 1,
    phase: "AUGMENTATION_SELECTION",
    castlingRights: createCastlingRights(),
    enPassantTarget: null,
    halfMoveClock: 0,
    positionHistory: [],
    actionHistory: [],
    consecutiveChecks: { white: 0, black: 0 },
    augmentations: {
      white: createPlayerAugmentations(),
      black: createPlayerAugmentations(),
    },
    augmentationSelection: createAugmentationSelection(),
    augmentationRound: 0,
    createdAt: now,
    updatedAt: now,
  };

  // Compute initial position hash and add to history
  state.positionHistory = [getPositionHash(state)];

  return state;
}

/**
 * Alias for createInitialState to match standard engine interface.
 * All game engines export initGame() as the standard initialization function.
 */
export const initGame = createInitialState;
